import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Stack, Alert, CircularProgress,
  MenuItem, Typography, Divider, Paper,
} from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';

import { useSimularWebhook } from '../../hooks/useTransacciones';
import { useCvus } from '../../hooks/useCvus';
import type { SimularWebhookResult } from '../../types/transaccion';
import { MONTO_MAX_DEFAULT } from '../../utils/validations';

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Simulador de webhook de Pronto Pago — solo dev.
 *
 * Construye un payload sintético y lo dispara contra el endpoint público
 * del webhook a través de /api/dev/simular-webhook-pronto-pago (que el
 * backend gating por NODE_ENV).
 *
 * Usalo para probar el flow end-to-end sin esperar una transferencia real:
 *   1. Generá un CVU para una empresa.
 *   2. Acá: elegí ese CVU, indicá monto y referencia.
 *   3. Disparar → aparece como transacción pre_confirmada.
 *   4. Conciliá contra un cupón → se crea el pago y se cierra.
 */
export default function SimuladorWebhookDialog({ open, onClose }: Props) {
  const { data: cvusResult } = useCvus();
  const cvus = cvusResult?.cvus ?? [];
  const simular = useSimularWebhook();

  const [cvuId, setCvuId] = useState('');
  const [referencia, setReferencia] = useState('');
  const [monto, setMonto] = useState('');
  const [result, setResult] = useState<SimularWebhookResult | null>(null);

  useEffect(() => {
    if (open) {
      setCvuId('');
      setReferencia(`PP-SIM-${Date.now()}`);
      setMonto('');
      setResult(null);
      simular.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const cvuElegido = cvus.find((c) => c.id === cvuId);

  const handleDisparar = async () => {
    if (!cvuElegido) return;
    const m = parseFloat(monto);
    // Defensa #031: monto positivo, finito, dentro de tope técnico.
    if (!isFinite(m) || m <= 0 || m > MONTO_MAX_DEFAULT) return;
    const refLimpia = referencia.trim();
    if (refLimpia.length < 3 || refLimpia.length > 200) return;

    try {
      const r = await simular.mutateAsync({
        referencia: refLimpia,
        externalId: cvuElegido.external_id ?? undefined,
        cvu:        cvuElegido.cvu,
        monto:      m,
      });
      setResult(r);
    } catch {
      // se muestra abajo
    }
  };

  const apiError = simular.error as Error | null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <ScienceIcon color="warning" />
          <span>Simulador de webhook Pronto Pago</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="warning" sx={{ fontSize: 13 }}>
            Solo para desarrollo y QA. Dispara un POST contra el endpoint del
            webhook como si viniera de Pronto Pago. <strong>No genera dinero
            real</strong>, solo una transacción en la BD local.
          </Alert>

          {apiError && <Alert severity="error">{apiError.message}</Alert>}

          <TextField
            select
            label="CVU receptor"
            required
            fullWidth
            value={cvuId}
            onChange={(e) => setCvuId(e.target.value)}
            disabled={cvus.length === 0}
            helperText={
              cvus.length === 0
                ? 'No hay CVUs cargadas. Generá una primero en /cvus.'
                : 'La transacción quedará asociada a la empresa de este CVU.'
            }
          >
            <MenuItem value=""><em>Seleccioná un CVU…</em></MenuItem>
            {cvus.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.empresa?.razon_social ?? '—'} — {c.alias}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Referencia externa"
            required
            fullWidth
            value={referencia}
            onChange={(e) => setReferencia(e.target.value)}
            helperText="Identificador único (3-200 chars). Si repetís la misma → idempotencia (duplicated=true)."
            inputProps={{ maxLength: 200 }}
          />

          <TextField
            label="Monto"
            type="number"
            required
            fullWidth
            value={monto}
            onChange={(e) => setMonto(e.target.value)}
            inputProps={{ min: 0.01, max: MONTO_MAX_DEFAULT, step: 0.01 }}
            helperText={`Máximo $${MONTO_MAX_DEFAULT.toLocaleString('es-AR')}`}
          />

          {result && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Respuesta del webhook
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Stack spacing={0.5}>
                <Typography variant="body2">
                  HTTP status: <strong>{result.webhook_status}</strong>
                </Typography>
                {result.webhook_response.duplicated ? (
                  <Alert severity="info" sx={{ fontSize: 13 }}>
                    Referencia duplicada — idempotencia funcionando OK. No se
                    creó transacción nueva.
                  </Alert>
                ) : (
                  <>
                    <Typography variant="body2">
                      Estado: <strong>{result.webhook_response.estado}</strong>
                    </Typography>
                    {result.webhook_response.transaccion_id && (
                      <Typography variant="body2">
                        transaccion_id:{' '}
                        <code>{result.webhook_response.transaccion_id}</code>
                      </Typography>
                    )}
                  </>
                )}
              </Stack>
            </Paper>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={simular.isPending}>
          Cerrar
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleDisparar}
          disabled={!cvuId || !monto || simular.isPending}
          startIcon={
            simular.isPending ? <CircularProgress size={18} color="inherit" /> : <ScienceIcon />
          }
        >
          {simular.isPending ? 'Disparando…' : 'Disparar webhook'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
